"use client";

import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { webSiteNavigationLabels } from "../webSiteNavigation";
import { useWebMobileMenuOverlay } from "./useWebMobileMenuOverlay";

export function StudioCleanRuntime({
    languageCode,
}: {
    languageCode?: string | null;
}) {
    const navigationId = useId();
    const sentinelRef = useRef<HTMLSpanElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);
    const [header, setHeader] = useState<HTMLElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const labels = webSiteNavigationLabels(languageCode);

    useEffect(() => {
        const template = sentinelRef.current?.closest<HTMLElement>(
            '[data-web-template="studio-clean"]',
        ) ?? null;
        const nextHeader = template?.querySelector<HTMLElement>(
            '[data-web-region="header"]',
        ) ?? null;
        const view = template?.ownerDocument.defaultView;

        setHeader(nextHeader);

        if (!template || !nextHeader || !view) {
            return;
        }

        const reducedMotion = view.matchMedia(
            "(prefers-reduced-motion: reduce)",
        ).matches;
        const revealSelector = '[data-web-builder-region="page"] .site-builder-section';
        let animationFrame: number | null = null;

        const updateHeader = () => {
            animationFrame = null;
            nextHeader.toggleAttribute(
                "data-studio-header-scrolled",
                view.scrollY > 12,
            );
        };
        const requestHeaderUpdate = () => {
            if (animationFrame === null) {
                animationFrame = view.requestAnimationFrame(updateHeader);
            }
        };

        updateHeader();
        view.addEventListener("scroll", requestHeaderUpdate, { passive: true });

        if (template.dataset.webTemplateEditor === "true") {
            return () => {
                view.removeEventListener("scroll", requestHeaderUpdate);
                if (animationFrame !== null) {
                    view.cancelAnimationFrame(animationFrame);
                }
                nextHeader.removeAttribute("data-studio-header-scrolled");
            };
        }

        const observer = !reducedMotion && "IntersectionObserver" in view
            ? new view.IntersectionObserver((entries, revealObserver) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) continue;

                    entry.target.setAttribute("data-studio-revealed", "true");
                    revealObserver.unobserve(entry.target);
                }
            }, {
                rootMargin: "0px 0px -8% 0px",
                threshold: 0.08,
            })
            : null;
        const registered = new Set<HTMLElement>();
        const register = (root: ParentNode) => {
            const candidates = [
                ...(root instanceof view.HTMLElement && root.matches(revealSelector)
                    ? [root]
                    : []),
                ...Array.from(root.querySelectorAll<HTMLElement>(revealSelector)),
            ];

            for (const candidate of candidates) {
                if (registered.has(candidate)) continue;

                registered.add(candidate);
                candidate.setAttribute("data-studio-reveal", "true");

                if (observer) {
                    observer.observe(candidate);
                } else {
                    candidate.setAttribute("data-studio-revealed", "true");
                }
            }
        };

        register(template);
        template.setAttribute("data-studio-runtime-ready", "true");

        const mutationObserver = new view.MutationObserver((entries) => {
            for (const entry of entries) {
                for (const node of entry.addedNodes) {
                    if (node instanceof view.HTMLElement) register(node);
                }
            }
        });

        mutationObserver.observe(template, { childList: true, subtree: true });

        return () => {
            view.removeEventListener("scroll", requestHeaderUpdate);
            observer?.disconnect();
            mutationObserver.disconnect();
            if (animationFrame !== null) view.cancelAnimationFrame(animationFrame);
            nextHeader.removeAttribute("data-studio-header-scrolled");
            template.removeAttribute("data-studio-runtime-ready");
            for (const element of registered) {
                element.removeAttribute("data-studio-reveal");
                element.removeAttribute("data-studio-revealed");
            }
        };
    }, []);

    useWebMobileMenuOverlay({
        header,
        isOpen,
        navigationId,
        openAttribute: "data-studio-mobile-menu-open",
        setIsOpen,
        templateCode: "studio-clean",
        toggleRef,
    });

    return (
        <>
            <span
                aria-hidden="true"
                data-studio-mobile-menu-runtime="true"
                ref={sentinelRef}
                style={{ display: "none" }}
            />
            {header
                ? createPortal(
                    <button
                        aria-controls={navigationId}
                        aria-expanded={isOpen}
                        aria-label={isOpen ? labels.closeMenu : labels.openMenu}
                        className="studio-mobile-menu-toggle"
                        onClick={(event) => {
                            event.stopPropagation();
                            setIsOpen((current) => !current);
                        }}
                        onPointerDown={(event) => event.stopPropagation()}
                        ref={toggleRef}
                        type="button"
                    >
                        <span aria-hidden="true" />
                        <span aria-hidden="true" />
                        <span aria-hidden="true" />
                    </button>,
                    header,
                )
                : null}
        </>
    );
}
